import { Link } from "react-router-dom";
import SEO from "../../components/SEO";
import FadeInSection from "../../components/FadeInSection";

export default function SeniorManagement() {
  const seniorManagementSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Senior Management",
    "url": "https://www.thrombio.com.au/leadership/senior-management",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "Organization",
      "name": "Thrombio",
      "description": "Thrombio is a drug discovery company dedicated to advancing the global standard of care for the treatment of stroke using novel anti-thrombotic drugs with unprecedented safety and efficacy."
    }
  };

  return (
    <div className="seniorManagementBg">
      <SEO
        title="Senior Management"
        description="Learn about our team of international leaders in stroke clinical trials, scientists with extensive drug development experience and businesspeople with more than 30 years' experience"
        keywords={['Leadership Team', 'Management', 'Peter Bush', 'Dr. Sundeep Dugar', 'Simone Schoenwaelder', 'Amos Meltzer', 'Simon Jackson']}
        ogImage="/peterBush.webp"
        schema={seniorManagementSchema}
      />

      <div className="page-width px-12 pt-44">
        <div className="flex flex-col gap-4 md:gap-16">
          {/* Optimized H1 tag that preserves layout */}
          <div className="text-[4.8rem] font-bold leading-[5.2rem] tracking-[.19rem] drop-shadow-[0_4px_8.9px_#000]">
            <FadeInSection>
              <h1 className="sr-only">ThromBio Senior Management: Leading Experts in Stroke Therapy</h1>
              Leadership
            </FadeInSection>
          </div>
          {/* Optimized H2 tag that preserves layout */}
          <div className="drop-shadow-[0_4px_8.9px_#000] font-syne text-[3rem] font-bold leading-[3.6rem] tracking-[.15rem]">
            <FadeInSection className="delay-500">
              <h2 className="sr-only">Our Senior Management Team: Pioneers in Stroke Treatment</h2>
              Senior Management
            </FadeInSection>
          </div>
        </div>
      </div>
      <div className="pt-[130px] xl:pt-[90px] xl:pb-[320px] xl:mb-[200px] px-16 xl:page-width xl:px-0 flex flex-col xl:relative xl:h-[1240px] items-center xl:items-start gap-[115px] md:gap-[75px] seniorManagementBg2 pb-[8rem] md:pb-[4rem]">
        {/* Row 1: Peter Bush - Center */}
        <div className="xl:absolute xl:top-[20px] xl:left-1/2 xl:-translate-x-1/2">
          <FadeInSection className="delay-1000" alwaysAnimate>
            <Link to="/leadership/peter-bush" className="group">
              <div className="flex flex-col items-center group">
                <img
                  src="/peterBush.webp"
                  alt="Peter Bush, Chief Executive Officer: Executive and Investor in Healthcare and Technology Sectors"
                  className="mb-20 transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                  width={241}
                  height={241}
                />
                <div className="text-center text-white text-[3.1rem] font-medium tracking-wider [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)]">
                  Peter Bush
                </div>
                <div className="text-center justify-start text-[#e0ffff] text-[2.1rem] font-syne font-bold leading-[2.6rem] tracking-[.096rem] [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)] mb-10">
                  Chief Executive Officer
                </div>
              </div>
            </Link>
          </FadeInSection>
        </div>
        <img src="/1.2.svg" alt="Decorative divider" width={23} height={14} className="xl:hidden" />
        
        {/* Row 2: Simone - Left */}
        <div className="xl:absolute xl:top-[480px] xl:left-[calc(50%-265px)] xl:-translate-x-1/2">
          <FadeInSection>
            <Link to="/leadership/simone-schoenwaelder" className="group">
              <div className="flex flex-col items-center group">
                <img
                  src="/simoneSchoenwaelder.webp"
                  alt="Simone Schoenwaelder, Head of Biology: Leading Innovator in Platelet Biology and Stroke Therapy"
                  className="mb-20 transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                  width={241}
                  height={241}
                />
                <div className="text-center text-white text-[3.1rem] font-medium tracking-wider [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)]">
                  Simone Schoenwaelder
                </div>
                <div className="text-center justify-start text-[#e0ffff] text-[2.1rem] font-syne font-bold leading-[2.6rem] tracking-[.096rem] [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)] mb-10">
                  Head, Biology
                </div>
              </div>
            </Link>
          </FadeInSection>
        </div>
        <img src="/1.2.svg" alt="Decorative divider" width={23} height={14} className="xl:hidden" />
        
        {/* Row 2: Simon - Right */}
        <div className="xl:absolute xl:top-[480px] xl:left-[calc(50%+265px)] xl:-translate-x-1/2">
          <FadeInSection className="xl:delay-[500ms]">
            <Link to="/leadership/simon-jackson" className="group">
              <div className="flex flex-col items-center group">
                <img
                  src="/SimonJackson.webp"
                  alt="Simon Jackson, Chief Information Officer: Technology Leader in Digital Transformation and Strategic Planning"
                  className="mb-20 transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                  width={241}
                  height={241}
                />
                <div className="text-center text-white text-[3.1rem] font-medium tracking-wider [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)]">
                  Simon Jackson
                </div>
                <div className="text-center justify-start text-[#e0ffff] text-[2.1rem] font-syne font-bold leading-[2.6rem] tracking-[.096rem] [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)] mb-10">
                  Chief Information Officer
                </div>
              </div>
            </Link>
          </FadeInSection>
        </div>
        <img src="/1.2.svg" alt="Decorative divider" width={23} height={14} className="xl:hidden" />
        
        {/* Row 3: Sundeep - Left */}
        <div className="xl:absolute xl:top-[940px] xl:left-[calc(50%-265px)] xl:-translate-x-1/2">
          <FadeInSection>
            <Link to="/leadership/sundeep-dugar" className="group">
              <div className="flex flex-col items-center group">
                <img
                  src="/sundeepDugar.webp"
                  alt="Dr. Sundeep Dugar, Drug Discovery: Renowned Leader in Oncology, Cardiovascular, and Metabolic Drug Development"
                  className="mb-20 transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                  width={241}
                  height={241}
                />
                <div className="text-center text-white text-[3.1rem] font-medium tracking-wider [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)]">
                  Dr. Sundeep Dugar
                </div>
                <div className="text-center justify-start text-[#e0ffff] text-[2.1rem] font-syne font-bold leading-[2.6rem] tracking-[.096rem] [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)] mb-10">
                  Drug Discovery
                </div>
              </div>
            </Link>
          </FadeInSection>
        </div>
        <img src="/1.2.svg" alt="Decorative divider" width={23} height={14} className="xl:hidden" />
        
        {/* Row 3: Amos - Right */}
        <div className="xl:absolute xl:top-[940px] xl:left-[calc(50%+265px)] xl:-translate-x-1/2 mb-[40px] md:mb-0">
          <FadeInSection className="xl:delay-[500ms]">
            <Link to="/leadership/amos-meltzer" className="group">
              <div className="flex flex-col items-center group">
                <img
                  src="/amosMeltzer.webp"
                  alt="Amos Meltzer, Corporate Development: Expert in Life Sciences, Intellectual Property, and Technology Commercialisation"
                  className="mb-20 transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                  width={241}
                  height={241}
                />
                <div className="text-center text-white text-[3.1rem] font-medium tracking-wider [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)]">
                  Amos Meltzer
                </div>
                <div className="text-center justify-start text-[#e0ffff] text-[2.1rem] font-syne font-bold leading-[2.6rem] tracking-[.096rem] [text-shadow:_2px_4px_5px_rgb(0_0_0_/_1.00)] mb-10">
                  Corporate Development
                </div>
              </div>
            </Link>
          </FadeInSection>
        </div>
      </div>
    </div>
  );
}